import React, { useState, useEffect } from "react";
import initialData from "./data.js";

export default function PrivateHealthCheck() {
  const [formData, setFormData] = useState(initialData);

  // ---------- helpers ----------
  const setNested = (path, value) => {
    setFormData((prev) => {
      const clone = structuredClone(prev);
      let obj = clone;
      const keys = Array.isArray(path) ? path : path.split(".");
      const last = keys.pop();
      for (const k of keys) {
        if (obj[k] == null) obj[k] = {};
        obj = obj[k];
      }
      obj[last] = value;
      return clone;
    });
  };

  const handleSimpleChange = (e) => {
    const { name, value } = e.target;
    setNested(name, value);
  };

  const handleNumberChange = (e) => {
    const { name, value } = e.target;
    const num = value === "" ? "" : Number(value);
    setNested(name, num);
  };

  const addArrayItem = (path, defaultValue) => {
    setFormData((prev) => {
      const clone = structuredClone(prev);
      const keys = path.split(".");
      const last = keys.pop();
      let obj = clone;
      for (const k of keys) obj = obj[k];
      if (!Array.isArray(obj[last])) obj[last] = [];
      obj[last].push(defaultValue);
      return clone;
    });
  };

  const updateArrayItem = (path, index, key, value) => {
    setFormData((prev) => {
      const clone = structuredClone(prev);
      const keys = path.split(".");
      const last = keys.pop();
      let obj = clone;
      for (const k of keys) obj = obj[k];
      obj[last][index][key] = value;
      return clone;
    });
  };

  const updateArrayStringItem = (path, index, value) => {
    setFormData((prev) => {
      const clone = structuredClone(prev);
      const keys = path.split(".");
      const last = keys.pop();
      let obj = clone;
      for (const k of keys) obj = obj[k];
      obj[last][index] = value;
      return clone;
    });
  };

  const removeArrayItem = (path, index) => {
    setFormData((prev) => {
      const clone = structuredClone(prev);
      const keys = path.split(".");
      const last = keys.pop();
      let obj = clone;
      for (const k of keys) obj = obj[k];
      obj[last].splice(index, 1);
      return clone;
    });
  };

  const saveToLocalStorage = () => {
    localStorage.setItem("hc_formData", JSON.stringify(formData));
    alert("Saved to localStorage");
  };

  const loadFromLocalStorage = () => {
    const str = localStorage.getItem("hc_formData");
    if (!str) return alert("Nothing in localStorage yet.");
    try {
      setFormData(JSON.parse(str));
      alert("Loaded from localStorage");
    } catch (e) {
      alert("Invalid JSON in localStorage");
    }
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(formData, null, 2));
      alert("Copied JSON to clipboard");
    } catch (e) {
      alert("Failed to copy");
    }
  };

  const downloadJSON = () => {
    const blob = new Blob([JSON.stringify(formData, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.download = `health-check-${(formData.clientName || "client")
      .toLowerCase()
      .replace(/\s+/g, "-")}.json`;
    a.href = url;
    a.click();
    URL.revokeObjectURL(url);
  };

  // auto-fill date if empty
  useEffect(() => {
    if (!formData.date) {
      setNested("date", new Date().toLocaleDateString());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen bg-gray-50 p-6 pt-24">
      <div className="max-w-5xl mx-auto space-y-10">

        <header className="flex items-center justify-between">
          <h1 className="text-2xl font-bold">Private Health Check Editor</h1>

          <div className="flex gap-2">
            <button
              onClick={saveToLocalStorage}
              className="bg-blue-600 text-white px-3 py-2 rounded hover:bg-blue-700 text-sm"
            >
              Save (localStorage)
            </button>
            <button
              onClick={loadFromLocalStorage}
              className="bg-slate-600 text-white px-3 py-2 rounded hover:bg-slate-700 text-sm"
            >
              Load (localStorage)
            </button>
            <button
              onClick={copyToClipboard}
              className="bg-emerald-600 text-white px-3 py-2 rounded hover:bg-emerald-700 text-sm"
            >
              Copy JSON
            </button>
            <button
              onClick={downloadJSON}
              className="bg-indigo-600 text-white px-3 py-2 rounded hover:bg-indigo-700 text-sm"
            >
              Download JSON
            </button>
          </div>
        </header>

        {/* Basic meta */}
        <Card title="Basic Info">
          <Text name="clientName" label="Client Name" value={formData.clientName} onChange={handleSimpleChange} />
          <Text name="url" label="URL" value={formData.url} onChange={handleSimpleChange} />
          <Text name="date" label="Date" value={formData.date} onChange={handleSimpleChange} />
        </Card>

        {/* Executive Scorecard */}
        <Card title="Executive Scorecard">
          <div className="grid md:grid-cols-2 gap-6">
            <ExecSection
              section="Performance"
              basePath="executive.performance"
              obj={formData.executive.performance}
              onChange={setNested}
            />
            <ExecSection
              section="SEO"
              basePath="executive.seo"
              obj={formData.executive.seo}
              onChange={setNested}
              showIssues
            />
            <ExecSection
              section="Mobile"
              basePath="executive.mobile"
              obj={formData.executive.mobile}
              onChange={setNested}
              showIssues
            />
            <ExecSection
              section="Accessibility"
              basePath="executive.accessibility"
              obj={formData.executive.accessibility}
              onChange={setNested}
              onlyGrade
            />
            <ExecSection
              section="Security"
              basePath="executive.security"
              obj={formData.executive.security}
              onChange={setNested}
              onlyGrade
            />
            <ExecSection
              section="Web Vitals"
              basePath="executive.webVitals"
              obj={formData.executive.webVitals}
              onChange={setNested}
              customPrimaryKey="passRate"
            />
          </div>
        </Card>

        {/* Core Web Vitals */}
        <Card title="Core Web Vitals">
          <VitalEditor
            label="LCP"
            basePath="vitals.lcp"
            obj={formData.vitals.lcp}
            onChange={setNested}
          />
          <VitalEditor
            label="INP"
            basePath="vitals.inp"
            obj={formData.vitals.inp}
            onChange={setNested}
          />
          <VitalEditor
            label="CLS"
            basePath="vitals.cls"
            obj={formData.vitals.cls}
            onChange={setNested}
          />
        </Card>

        {/* Severity */}
        <Card title="Severity Summary">
          <Number
            name="severity.critical"
            label="Critical"
            value={formData.severity.critical}
            onChange={handleNumberChange}
          />
          <Number
            name="severity.high"
            label="High"
            value={formData.severity.high}
            onChange={handleNumberChange}
          />
          <Number
            name="severity.medium"
            label="Medium"
            value={formData.severity.medium}
            onChange={handleNumberChange}
          />
          <Number
            name="severity.low"
            label="Low"
            value={formData.severity.low}
            onChange={handleNumberChange}
          />
        </Card>

        {/* Quick Wins */}
        <Card title="Top 5 Quick Wins">
          {formData.quickWins.map((item, i) => (
            <div key={i} className="border p-3 rounded mb-3 space-y-2">
              <div className="grid md:grid-cols-3 gap-2">
                <TextInline
                  label="Priority"
                  value={item.priority}
                  onChange={(v) => updateArrayItem("quickWins", i, "priority", v)}
                />
                <TextInline
                  label="Task"
                  value={item.task}
                  onChange={(v) => updateArrayItem("quickWins", i, "task", v)}
                />
                <TextInline
                  label="Impact"
                  value={item.impact}
                  onChange={(v) => updateArrayItem("quickWins", i, "impact", v)}
                />
              </div>
              <button
                className="text-sm text-red-600 hover:underline"
                onClick={() => removeArrayItem("quickWins", i)}
              >
                Remove
              </button>
            </div>
          ))}

          <button
            className="mt-2 bg-slate-200 hover:bg-slate-300 text-slate-800 px-3 py-1 rounded text-sm"
            onClick={() =>
              addArrayItem("quickWins", {
                priority: "Medium",
                task: "",
                impact: "Medium",
              })
            }
          >
            + Add Quick Win
          </button>
        </Card>

        {/* ROI */}
        <Card title="ROI Snapshot (lines)">
          {formData.roi.map((line, i) => (
          <div key={i} className="flex items-center gap-2 mb-2">
            <input
              type="text"
              value={line}
              onChange={(e) => updateArrayStringItem("roi", i, e.target.value)}
              className="border p-2 rounded w-full"
            />
            <button
              onClick={() => removeArrayItem("roi", i)}
              className="text-red-600 text-sm hover:underline"
            >
              Remove
            </button>
          </div>
          ))}
          <button
            className="mt-2 bg-slate-200 hover:bg-slate-300 text-slate-800 px-3 py-1 rounded text-sm"
            onClick={() => addArrayItem("roi", "")}
          >
            + Add ROI line
          </button>
        </Card>

        {/* Why It Matters */}
        <Card title="Why it matters (lines)">
          {formData.whyItMatters.map((line, i) => (
          <div key={i} className="flex items-center gap-2 mb-2">
            <input
              type="text"
              value={line}
              onChange={(e) =>
                updateArrayStringItem("whyItMatters", i, e.target.value)
              }
              className="border p-2 rounded w-full"
            />
            <button
              onClick={() => removeArrayItem("whyItMatters", i)}
              className="text-red-600 text-sm hover:underline"
            >
              Remove
            </button>
          </div>
          ))}
          <button
            className="mt-2 bg-slate-200 hover:bg-slate-300 text-slate-800 px-3 py-1 rounded text-sm"
            onClick={() => addArrayItem("whyItMatters", "")}
          >
            + Add line
          </button>
        </Card>

        {/* JSON Preview */}
        <Card title="Live JSON Preview">
          <pre className="text-xs bg-slate-900 text-green-200 p-4 rounded overflow-x-auto max-h-[400px]">
            {JSON.stringify(formData, null, 2)}
          </pre>
        </Card>
      </div>
    </div>
  );
}

/* ----------------- small reusable pieces ----------------- */

function Card({ title, children }) {
  return (
    <section className="bg-white rounded shadow p-4">
      <h2 className="text-lg font-semibold mb-4">{title}</h2>
      {children}
    </section>
  );
}

function Text({ name, label, value, onChange }) {
  return (
    <div className="mb-3">
      <label className="block text-sm font-medium mb-1">{label}</label>
      <input
        type="text"
        name={name}
        value={value ?? ""}
        onChange={onChange}
        className="border p-2 rounded w-full"
      />
    </div>
  );
}

function Number({ name, label, value, onChange }) {
  return (
    <div className="mb-3">
      <label className="block text-sm font-medium mb-1">{label}</label>
      <input
        type="number"
        name={name}
        value={value ?? 0}
        onChange={onChange}
        className="border p-2 rounded w-full"
      />
    </div>
  );
}

function TextInline({ label, value, onChange }) {
  return (
    <div>
      <label className="block text-xs font-medium mb-1">{label}</label>
      <input
        type="text"
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        className="border p-2 rounded w-full text-sm"
      />
    </div>
  );
}

function ExecSection({
  section,
  basePath,
  obj,
  onChange,
  showIssues = false,
  onlyGrade = false,
  customPrimaryKey,
}) {
  const primaryKey = customPrimaryKey || "score";
  return (
    <div className="border rounded p-3">
      <h4 className="font-semibold mb-2">{section}</h4>

      {!onlyGrade && !customPrimaryKey && (
        <Number
          name={`${basePath}.${primaryKey}`}
          label={primaryKey}
          value={obj?.[primaryKey]}
          onChange={(e) =>
            onChange(`${basePath}.${primaryKey}`, e.target.value === "" ? "" : Number(e.target.value))
          }
        />
      )}

      {customPrimaryKey && (
        <Text
          name={`${basePath}.${customPrimaryKey}`}
          label={customPrimaryKey}
          value={obj?.[customPrimaryKey] ?? ""}
          onChange={(e) => onChange(`${basePath}.${customPrimaryKey}`, e.target.value)}
        />
      )}

      {showIssues && (
        <Number
          name={`${basePath}.issues`}
          label="issues"
          value={obj?.issues ?? 0}
          onChange={(e) =>
            onChange(`${basePath}.issues`, e.target.value === "" ? "" : Number(e.target.value))
          }
        />
      )}

      <Text
        name={`${basePath}.grade`}
        label="grade"
        value={obj?.grade ?? ""}
        onChange={(e) => onChange(`${basePath}.grade`, e.target.value)}
      />
      <Text
        name={`${basePath}.note`}
        label="note"
        value={obj?.note ?? ""}
        onChange={(e) => onChange(`${basePath}.note`, e.target.value)}
      />
    </div>
  );
}

function VitalEditor({ label, basePath, obj, onChange }) {
  return (
    <div className="border rounded p-3 mb-3">
      <h4 className="font-semibold mb-2">{label}</h4>

      <Number
        name={`${basePath}.value`}
        label="value"
        value={obj?.value ?? 0}
        onChange={(e) =>
          onChange(`${basePath}.value`, e.target.value === "" ? "" : Number(e.target.value))
        }
      />

      <Number
        name={`${basePath}.target`}
        label="target"
        value={obj?.target ?? 0}
        onChange={(e) =>
          onChange(`${basePath}.target`, e.target.value === "" ? "" : Number(e.target.value))
        }
      />

      <Text
        name={`${basePath}.unit`}
        label="unit"
        value={obj?.unit ?? ""}
        onChange={(e) => onChange(`${basePath}.unit`, e.target.value)}
      />
    </div>
  );
}
